import React from 'react';
import { Control, useFieldArray } from 'react-hook-form';
import { AnyEmptyQuestion } from '../../../../../../types';
import { StyledQuestionFieldContainer } from '../../styles';
import { InputLabel } from '@material-ui/core';
import AnswerEditor from './AnswerEditor';
import { StyledAnswersList } from './styles';
import AddAnswerButton from './AddAnswerButton';
import { useDispatch, useSelector } from 'react-redux';
import { selectBlockingAnswers } from '../../../../../../../../redux/questionnaire/selectors';
import { addBlockingAnswer, removeBlockingAnswer } from '../../../../../../../../redux/questionnaire/actions';

interface Props {
  control: Control<AnyEmptyQuestion>;
  /**
   * False on an information screen: the answer stays editable, because clients that
   * predate answer_selection still draw it, but a second one would turn the screen into
   * a question with a choice on it.
   */
  canAddAnswers?: boolean;
  /**
   * False on an information screen. Only a client that still draws the answer can block
   * on it, so a flag here stops some patients and not others depending on which app they
   * are holding. Clearing an existing flag stays available, or an answer that was already
   * blocking when the selection was hidden leaves the author an error and no control to
   * fix it.
   */
  canAddBlockingAnswers?: boolean;
}

const AnswersEditor = ({ control, canAddAnswers = true, canAddBlockingAnswers = true }: Props) => {
  const { fields, append, remove } = useFieldArray({ control, name: 'answers' });
  const blockingAnswers = useBlockingAnswers();

  return (
    <StyledQuestionFieldContainer>
      <InputLabel>Answers</InputLabel>
      <StyledAnswersList>
        {fields.map((field, index) => {
          const isBlockingAnswer = blockingAnswers.isBlocking(field._id);

          const handleToggleIsBlocking = () => {
            if (!canAddBlockingAnswers && !isBlockingAnswer) return;

            blockingAnswers.toggle(field._id);
          };

          const handleRemove = () => {
            remove(index);
            blockingAnswers.remove(field._id);
          };

          return (
            <AnswerEditor
              canToggleIsBlocking={canAddBlockingAnswers || isBlockingAnswer}
              onToggleIsBlocking={handleToggleIsBlocking}
              isBlocking={isBlockingAnswer}
              onRemove={handleRemove}
              key={field._id}
              control={control}
              index={index}
            />
          );
        })}

        {canAddAnswers && <AddAnswerButton append={append} />}
      </StyledAnswersList>
    </StyledQuestionFieldContainer>
  );
};

const useBlockingAnswers = () => {
  const ids = useSelector(selectBlockingAnswers);
  const dispatch = useDispatch();

  const add = (answerId: string) => {
    dispatch(addBlockingAnswer(answerId));
  };

  const remove = (answerId: string) => {
    dispatch(removeBlockingAnswer(answerId));
  };

  const toggle = (answerId: string) => {
    if (ids.includes(answerId)) {
      remove(answerId);
    } else {
      add(answerId);
    }
  };

  const isBlocking = (answerId: string) => ids.includes(answerId);

  return { isBlocking, toggle, remove };
};

export default AnswersEditor;
